import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { lazy, Suspense, useEffect, useRef } from "react";
import { ErrorBoundary } from "@/components/common";
import { logInfo } from "@/lib/logger";
import { useAnalytics } from "@/hooks/useAnalytics";
import { Toaster } from "@strivelabs/ui-core";
import { useOrganization, useOrganizationList, useUser } from "@clerk/clerk-react";
import { STRIVELABS_USERS } from "@/constants";
import { ChartsDemo } from "./components/demo/ChartsDemo";
import { useTenantDetails } from "@/hooks/useConnections";
import { useAppStore } from "@/store/appStore";
import { ContainerDemo } from "./components/demo/ContainerDemo";
import { InsightsDemo } from "./components/demo/InsightsDemo";
import { ChatProvider } from "@/contexts/ChatContext";
import { ThemeProvider } from "@/hooks/useTheme";
import '@/lib/themes'; // Register all themes on app load

// Lazy loading pages for better performance
const SignIn = lazy(() => import("@/pages/SignIn"));
const SignUp = lazy(() => import("@/pages/SignUp"));
const HomeOrWorkforcePage = lazy(() => import("@/pages/HomeOrWorkforcePage"));
const MyWorkforce = lazy(() => import("@/pages/MyWorkforce"));
const Marketplace = lazy(() => import("@/pages/Marketplace"));
const Experiments = lazy(() => import("@/pages/Experiments"));
const Settings = lazy(() => import("@/pages/Settings"));
const AgentLayout = lazy(() => import("@/pages/AgentLayout"));
const InsightReport = lazy(() => import("@/pages/InsightReport"));
const AnswerEngineOptimization = lazy(() => import("@/agents/answer-engine-optimization/AnswerEngineOptimization"))
const Reports = lazy(() => import("@/pages/Reports"));
const ReportStudio = lazy(() => import("@/pages/ReportStudio"));
const Routines = lazy(() => import("@/pages/Routines"));
const KnowledgeHubLayout = lazy(() => import("@/pages/KnowledgeHub/KnowledgeHubLayout"));
const KnowledgeHubHome = lazy(() => import("@/pages/KnowledgeHub/KnowledgeHubHome"));
const KnowledgeHubFile = lazy(() => import("@/pages/KnowledgeHub/KnowledgeHubFile"));
const Skills = lazy(() => import("@/pages/Skills"));
const Glossary = lazy(() => import("@/pages/Glossary"));
const BlockKitPlayground = lazy(() => import("@/pages/BlockKitPlayground"));
const BlogPostStrategizerLayout = lazy(() => import("@/agents/blog-post-content-strategizer/BlogPostStrategizerLayout"));
const BlogPostRefresherLayout = lazy(() => import("@/agents/blog-post-content-refresher/BlogPostRefresherLayout"));
const SeoAuditLayout = lazy(() => import("@/agents/seo-audit/SeoAuditLayout"));
const NegativeKeywordAnalyzerLayout = lazy(() => import("@/agents/negative-keyword-analyzer/NegativeKeywordAnalyzerLayout"));
const PdfRenderer = lazy(() => import("@/components/pdf/PdfRenderer"));

// Onboarding flow components
const OnboardingLayout = lazy(() => import("@/pages/onboarding/OnboardingLayout").then(m => ({ default: m.OnboardingLayout })));
const WelcomeStep = lazy(() => import("@/pages/onboarding/WelcomeStep").then(m => ({ default: m.WelcomeStep })));
const CompanyInfoStep = lazy(() => import("@/pages/onboarding/CompanyInfoStep").then(m => ({ default: m.CompanyInfoStep })));
const BrandCompetitorsStep = lazy(() => import("@/pages/onboarding/BrandCompetitorsStep").then(m => ({ default: m.BrandCompetitorsStep })));
const ConnectDataStep = lazy(() => import("@/pages/onboarding/ConnectDataStep").then(m => ({ default: m.ConnectDataStep })));

// Post-onboarding setup (live build) page
const SetupLayout = lazy(() => import("@/pages/onboardingSetup/SetupLayout"));
const SetupHome = lazy(() => import("@/pages/onboardingSetup/SetupHome"));

// @demo: uncomment routes in demoRoutes.tsx to include them in the build
import { DemoRoutes } from "@/demoRoutes";

// Redirect that preserves query parameters (redirect_url, etc.)
function NavigatePreservingSearch({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

// Loading component for suspense fallback
const Loading = () => {
  logInfo('Loading component rendered', { component: 'App Loading' });
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent"></div>
    </div>
  );
};

// Layout component for protected routes
const ProtectedLayout = () => (
  <ErrorBoundary>
    <ProtectedRoute>
      <Outlet />
    </ProtectedRoute>
  </ErrorBoundary>
);

// Layout for internal-only routes — redirects non-StriveLabs users to home
const InternalRoute = () => {
  const { user, isLoaded } = useUser();
  if (!isLoaded) return null;
  const userEmail = user?.primaryEmailAddress?.emailAddress;
  if (userEmail && !STRIVELABS_USERS.includes(userEmail)) {
    return <Navigate to="/ui" replace />;
  }
  return <Outlet />;
};

export default function App() {
  logInfo('App component rendered');

  // setting identity to identify the user in analytics
  useAnalytics();

  // Auto-select and set the first available organization as active
  const { isLoaded: isActiveOrgDetailsLoaded, organization } = useOrganization();

  // Set tenant details in global store — only fetch when an org is active
  // (first-time users going through onboarding don't have an org/tenant yet)
  const { data: tenantDetails } = useTenantDetails({ enabled: Boolean(organization) });
  useEffect(() => {
    if (tenantDetails) {
      useAppStore.getState().setTenantDetails(tenantDetails);
      logInfo('App: Tenant details set in global store', { tenantId: tenantDetails.tenant.id });
    }
  }, [tenantDetails]);
  const { isLoaded: isOrgListLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  });
  const orgInitRef = useRef(false);

  useEffect(() => {
    // Avoid repeated execution
    if (orgInitRef.current) return;

    // If active org is already available, do nothing
    if (isActiveOrgDetailsLoaded && organization) {
      orgInitRef.current = true;
      return;
    }

    // Wait until both org context and membership list are loaded and data is fetched
    if (!isActiveOrgDetailsLoaded || !isOrgListLoaded || userMemberships?.isLoading) return;

    // If a local org is configured (development only), prefer it; otherwise pick the first membership
    const localClerkOrgId =
      import.meta.env.MODE === 'development' ? import.meta.env.VITE_LOCAL_CLERK_ORG_ID : undefined;
    const targetMembership =
      (localClerkOrgId &&
        userMemberships?.data?.find((m) => m.organization.id === localClerkOrgId)) ||
      userMemberships?.data?.[0];
    if (targetMembership?.organization) {
      setActive({ organization: targetMembership.organization.id })
        .then(() => {
          orgInitRef.current = true;
          logInfo('App: Set first organization as active', {
            orgId: targetMembership.organization.id,
          });
        })
        .catch(() => {
          // Non-blocking: just mark as initialized to prevent loops
          orgInitRef.current = true;
        });
    } else if (!userMemberships?.isFetching) {
      // No orgs available and not fetching more; mark as initialized
      orgInitRef.current = true;
    }
  }, [isActiveOrgDetailsLoaded, isOrgListLoaded, organization, userMemberships, setActive]);

  return (
    <ThemeProvider>
    <ChatProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <Suspense fallback={<Loading />}>
            <Routes>
              {/* Protected Routes */}
              <Route element={<ProtectedLayout />}>
                <Route path="/ui" element={<HomeOrWorkforcePage />} />
                <Route path="/ui/chat" element={<HomeOrWorkforcePage />} />
                <Route path="/ui/chat/:executionId" element={<HomeOrWorkforcePage />} />
                <Route path="/ui/my-workforce" element={<MyWorkforce />} />
                <Route element={<InternalRoute />}>
                  <Route path="/ui/marketplace" element={<Marketplace />} />
                  <Route path="/ui/experiments" element={<Experiments />} />
                </Route>
                <Route path="/ui/settings" element={<Settings />} />
                <Route path="/ui/routines" element={<Routines />} />
                <Route path="/ui/skills" element={<Skills />} />
                <Route path="/ui/glossary" element={<Glossary />} />
                <Route path="/ui/reports" element={<Reports />} />
                <Route path="/ui/report-studio/:insightId" element={<ReportStudio />} />
                <Route path="/ui/report-studio/:insightId/:executionId" element={<ReportStudio />} />
                <Route path="/ui/reports/:insightId" element={<InsightReport />} />
                <Route path="/ui/knowledge-hub" element={<KnowledgeHubLayout />}>
                  <Route index element={<KnowledgeHubHome />} />
                  <Route path=":knowledgeHubId" element={<KnowledgeHubFile />} />
                </Route>
                <Route path="/ui/onboarding/setup" element={<SetupLayout />}>
                  <Route index element={<SetupHome />} />
                </Route>
                <Route path="/ui/agent/answer-engine-optimization" element={<AnswerEngineOptimization />} />
                <Route path="/ui/agent/blog-post-content-strategizer/*" element={<BlogPostStrategizerLayout />} />
                <Route path="/ui/agent/blog-post-content-refresher/*" element={<BlogPostRefresherLayout />} />
                <Route path="/ui/agent/seo-audit/*" element={<SeoAuditLayout />} />
                <Route path="/ui/agent/negative-keyword-analyzer/*" element={<NegativeKeywordAnalyzerLayout />} />

                {/* @demo: uncomment routes in demoRoutes.tsx to activate them */}
                {DemoRoutes}

                <Route path="/ui/agent/:agentId" element={<AgentLayout />} />
              </Route>

              {/* Public/Special Routes with ErrorBoundary only */}
              <Route path="/ui/charts" element={
                <ErrorBoundary>
                  <ChartsDemo />
                </ErrorBoundary>
              } />
              <Route path="/ui/slots" element={
                <ErrorBoundary>
                  <ContainerDemo />
                </ErrorBoundary>
              } />
              <Route path="/ui/insights" element={
                <ErrorBoundary>
                  <InsightsDemo />
                </ErrorBoundary>
              } />
              <Route path="/ui/block-kit-playground" element={
                <ErrorBoundary>
                  <BlockKitPlayground />
                </ErrorBoundary>
              } />
              <Route path="/ui/pdf-renderer" element={
                <ErrorBoundary>
                  <PdfRenderer />
                </ErrorBoundary>
              } />
              <Route path="/ui/onboarding" element={
                <ErrorBoundary>
                  <OnboardingLayout />
                </ErrorBoundary>
              }>
                <Route index element={<NavigatePreservingSearch to="welcome" />} />
                <Route path="welcome" element={<WelcomeStep />} />
                <Route path="company-info" element={<CompanyInfoStep />} />
                <Route path="brand" element={<BrandCompetitorsStep />} />
                <Route path="connect" element={<ConnectDataStep />} />
              </Route>
              <Route path="/ui/sign-in" element={
                <ErrorBoundary>
                  <SignIn />
                </ErrorBoundary>
              } />
              <Route path="/ui/sign-up" element={
                <ErrorBoundary>
                  <SignUp />
                </ErrorBoundary>
              } />
            </Routes>
          </Suspense>
        </ErrorBoundary>
        <Toaster />
      </BrowserRouter>
    </ChatProvider>
    </ThemeProvider>
  );
}
